import { useContext, useEffect, useMemo } from 'react';
import type { Control, FieldValues, Path } from 'react-hook-form';
import { useForm } from 'react-hook-form';

import type { ComboboxOption } from '@/components/ui/combobox';
import { Combobox } from '@/components/ui/combobox';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { AgentAuthorityContext } from '@/lib/layout/AgentAuthorityContext';
import { REMOVED_LOCATION_LABEL } from '@/lib/pages/forms/components/constants';
import { ReadOnlyField } from '@/lib/pages/forms/components/ReadOnlyField';
import { cn } from '@/lib/utils';

export type SelectableLocation<T extends string> = {
  value: T;
  label: string;
  primary?: boolean | undefined;
};

// New interface for the searchable location select
export type LocationOption = ComboboxOption;

export interface SearchableLocationSelectProps<T extends FieldValues> {
  control: Control<T>;
  options: LocationOption[];
  name: Path<T> | string;
  label?: string;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  disabled?: boolean;
  orientation?: 'horizontal' | 'vertical';
  required?: boolean;
  stretch?: boolean;
  wideLabel?: boolean;
  readOnly?: boolean;
  overwriteLabelWidth?: string;
  overwriteInputWidth?: string;
  labelClassName?: string;
  nativeRequired?: boolean;
  ignoreReadMode?: boolean;
}

export const SearchableLocationSelect = <T extends FieldValues>({
  control,
  options,
  name,
  label,
  placeholder = 'Select location...',
  searchPlaceholder = 'Search locations...',
  emptyMessage = 'No locations found.',
  disabled,
  orientation = 'vertical',
  required,
  stretch = false,
  wideLabel,
  readOnly,
  overwriteLabelWidth,
  overwriteInputWidth,
  labelClassName,
  nativeRequired = false,
  ignoreReadMode = false,
}: SearchableLocationSelectProps<T>) => {
  const { responsive, editMode } = useContext(AgentAuthorityContext);

  // Helper function to calculate input container classes
  const getInputContainerClasses = () => {
    if (orientation === 'vertical') return '';

    let baseWidth = '';
    if (overwriteInputWidth) {
      baseWidth = overwriteInputWidth;
    } else if (stretch) {
      baseWidth = wideLabel ? 'w-1/3' : 'w-2/3';
    } else {
      baseWidth = wideLabel ? 'w-1/5' : 'w-2/5';
    }

    return cn(baseWidth, responsive && 'max-md:w-full', '!mt-0');
  };

  return (
    <FormField
      control={control}
      name={name as Path<T>}
      render={({ field }) => {
        if ((!editMode && !ignoreReadMode) || readOnly) {
          const optionLabel =
            (options || []).find(
              (option) => String(option.value) === String(field.value)
            )?.label ?? (field.value ? REMOVED_LOCATION_LABEL : '');
          return (
            <ReadOnlyField
              label={label}
              value={optionLabel}
              orientation={orientation}
              stretch={stretch}
              wideLabel={wideLabel}
              overwriteInputWidth={overwriteInputWidth}
              overwriteLabelWidth={overwriteLabelWidth}
            />
          );
        }
        const orphanedOptions =
          Boolean(field.value) &&
          !options.some((o) => String(o.value) === String(field.value))
            ? [{ value: field.value, label: REMOVED_LOCATION_LABEL }]
            : [];
        const resolvedOptions = [...orphanedOptions, ...options];

        return (
          <FormItem
            className={cn(
              'flex',
              orientation === 'horizontal'
                ? 'items-center gap-4'
                : 'w-full flex-col'
            )}
          >
            {label ? (
              <FormLabel
                className={cn(
                  (() => {
                    if (orientation !== 'horizontal') return 'mb-2';
                    if (overwriteLabelWidth) return overwriteLabelWidth;
                    if (stretch) return wideLabel ? 'w-2/3' : 'w-1/3';
                    return wideLabel ? 'w-2/5' : 'w-1/5';
                  })(),
                  'font-semibold leading-[1.5rem]',
                  labelClassName
                )}
              >
                {label}
                {(required || nativeRequired) && !disabled ? (
                  <span className="text-red-400">*</span>
                ) : null}
              </FormLabel>
            ) : null}
            <div className={getInputContainerClasses()}>
              <FormControl>
                <Combobox
                  options={resolvedOptions}
                  value={field.value ?? ''}
                  onValueChange={field.onChange}
                  placeholder={placeholder}
                  searchPlaceholder={searchPlaceholder}
                  disabled={disabled}
                  emptyMessage={emptyMessage}
                />
              </FormControl>
              <FormMessage />
            </div>
          </FormItem>
        );
      }}
    />
  );
};

// Legacy component for backward compatibility
export const AlternativeSelect = <T extends FieldValues, U extends string>({
  onChange,
  // Not the best solution, but component was meant to be used specifically for primary location select and has specific logic which we now need to skip to use in different context
  onChangeWithoutDefaultValue,
  options,
  name,
  label,
  placeholder,
  disabled,
  orientation = 'vertical',
  required,
  ignoreReadMode,
  stretch,
  value,
}: {
  onChange: (value: string) => void;
  onChangeWithoutDefaultValue?: (value: string) => void;
  options: Array<SelectableLocation<U>>;
  name: Path<T> | string;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  orientation?: 'horizontal' | 'vertical';
  required?: boolean;
  ignoreReadMode?: boolean;
  stretch?: boolean;
  value?: string;
}) => {
  const primaryLocation = options.find((option) => option.primary);
  const { editMode } = useContext(AgentAuthorityContext);
  const form = useForm({});

  useEffect(() => {
    if (!primaryLocation?.value && !value && options.length) {
      onChange(options[0].value);
    }
    if (primaryLocation?.value && !value && options.length) {
      onChange(primaryLocation.value);
    }
  }, [onChange, options, primaryLocation, value]);

  const defaultLocation = useMemo(() => {
    if (!primaryLocation?.value && !value && options.length === 1) {
      return options[0].value;
    }
    return '';
    // eslint-disable-next-line react-hooks/exhaustive-deps -- TODO: review deps in future refactor
  }, []);

  if (!editMode && !ignoreReadMode) {
    return (
      <Form {...form}>
        <ReadOnlyField label={label} value={primaryLocation?.label || ''} />
      </Form>
    );
  }

  return (
    <div
      className={`flex ${orientation === 'horizontal' ? 'items-center gap-4' : 'w-full flex-col'}`}
    >
      {label ? (
        <label
          className={`${orientation === 'horizontal' ? `${stretch ? 'w-1/3' : 'w-1/5'}` : 'mb-2'} font-semibold leading-[1.5rem]`}
        >
          {label}
          {required && !disabled ? (
            <span className="text-red-400">*</span>
          ) : null}
        </label>
      ) : null}
      <div
        className={`${orientation === 'horizontal' ? `${stretch ? 'w-2/3' : 'w-2/5'}` : ''} !mt-0`}
      >
        <Select
          value={value}
          onValueChange={onChangeWithoutDefaultValue || onChange}
          defaultValue={
            primaryLocation?.value || value || defaultLocation || ''
          }
          disabled={disabled}
        >
          <SelectTrigger tabIndex={0}>
            <SelectValue placeholder={placeholder} />
          </SelectTrigger>
          <SelectContent>
            {options.map(({ value, label }, idx) => (
              <SelectItem
                key={`${name}_${value}_${idx}`}
                value={value}
                className="min-h-6"
              >
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};

export { AlternativeSelect as LocationSelect };
